import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TimelineIcon from '@mui/icons-material/Timeline';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

function formatDate(value) {
  return formatDateDisplay(value, String(value || '-'));
}

function sexLabel(value, language) {
  const normalized = String(value || '').toLowerCase();
  if (['m', 'mare', 'female', 'filly'].includes(normalized)) {
    return language === 'ar' ? 'أنثى' : 'Female';
  }
  if (['s', 'stallion', 'horse', 'male', 'colt'].includes(normalized)) {
    return language === 'ar' ? 'ذكر' : 'Male';
  }
  return value || '-';
}

function StatCard({ label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 96 }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: soft, color, width: 44, height: 44 }}>
          <TimelineIcon />
        </Avatar>
        <Box>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value ?? '-'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function HorseProductionTimeline() {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const language = i18n.language === 'ar' ? 'ar' : 'en';
  const dir = language === 'ar' ? 'rtl' : 'ltr';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const labels = useMemo(
    () =>
      language === 'ar'
        ? {
            title: 'الخط الزمني للإنتاج',
            subtitle: 'عرض الأبناء المسجلين والمتاحين في الخط الزمني للإنتاج.',
            back: 'رجوع للتفاصيل',
            total: 'إجمالي الإنتاج',
            local: 'مسجل محلياً',
            external: 'AllBreed',
            latest: 'آخر سنة إنتاج',
            noData: 'لا توجد أبناء مسجلة لهذا الخيل حتى الآن.',
            localBadge: 'محلي',
            externalBadge: 'خارجي',
            openDetails: 'فتح التفاصيل',
            sire: 'الأب',
            dam: 'الأم',
            owner: 'المالك',
            rfid: 'الشريحة',
          }
        : {
            title: 'Production timeline',
            subtitle: 'View offspring records available in the production timeline.',
            back: 'Back to details',
            total: 'Total offspring',
            local: 'Local records',
            external: 'AllBreed',
            latest: 'Latest crop year',
            noData: 'No offspring records are available for this horse yet.',
            localBadge: 'Local',
            externalBadge: 'External',
            openDetails: 'Open details',
            sire: 'Sire',
            dam: 'Dam',
            owner: 'Owner',
            rfid: 'RFID',
          },
    [language]
  );

  const loadTimeline = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/horses/${horseId}/production-timeline`);
      setData(response.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load production timeline');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horseId]);

  const grouped = useMemo(() => {
    const buckets = new Map();
    (data?.timeline || []).forEach((item) => {
      const year = item.birth_year || (language === 'ar' ? 'غير محدد' : 'Unknown');
      if (!buckets.has(year)) buckets.set(year, []);
      buckets.get(year).push(item);
    });
    return [...buckets.entries()];
  }, [data, language]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box dir={dir} sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {labels.title}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {data?.horse?.name || '-'} {data?.horse?.name_arabic ? `- ${data.horse.name_arabic}` : ''}
              </Typography>
              <Typography sx={{ color: COLORS.gold, mt: 0.5 }}>
                {labels.subtitle}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={() => navigate(`/horse-details/${horseId}`)}
              sx={{ color: COLORS.text, borderColor: COLORS.line, fontWeight: 900 }}
            >
              {labels.back}
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 2,
            mb: 2.5,
          }}
        >
          <StatCard label={labels.total} value={data?.summary?.total_offspring || 0} />
          <StatCard label={labels.local} value={data?.summary?.local_offspring || 0} color={COLORS.green} soft={COLORS.greenSoft} />
          <StatCard label={labels.external} value={data?.summary?.allbreed_offspring || 0} color={COLORS.blue} soft={COLORS.blueSoft} />
          <StatCard label={labels.latest} value={data?.summary?.latest_year || '-'} color={COLORS.rose} soft={COLORS.roseSoft} />
        </Box>

        <Paper elevation={0} sx={{ ...cardSx, p: 2.5 }}>
          {!grouped.length ? (
            <Box sx={{ textAlign: 'center', color: COLORS.muted, py: 6 }}>{labels.noData}</Box>
          ) : (
            <Stack spacing={2.5}>
              {grouped.map(([year, items]) => (
                <Box key={year}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Avatar sx={{ bgcolor: COLORS.goldSoft, color: COLORS.gold, width: 38, height: 38 }}>
                      {year}
                    </Avatar>
                    <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                      {year}
                    </Typography>
                    <Chip size="small" label={`${items.length}`} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 900 }} />
                  </Stack>
                  <Stack spacing={1.2}>
                    {items.map((item) => (
                      <Paper key={item.id} elevation={0} sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2, p: 1.6, bgcolor: COLORS.card }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography sx={{ color: COLORS.text, fontWeight: 900, fontSize: 18 }}>
                                {item.name || '-'}
                              </Typography>
                              <Chip
                                size="small"
                                label={item.source === 'local' ? labels.localBadge : labels.externalBadge}
                                sx={{
                                  bgcolor: item.source === 'local' ? COLORS.greenSoft : COLORS.blueSoft,
                                  color: COLORS.text,
                                  fontWeight: 800,
                                }}
                              />
                              <Chip size="small" label={sexLabel(item.sex, language)} sx={{ bgcolor: COLORS.cardSoft, fontWeight: 800 }} />
                            </Stack>
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt: 0.8, color: COLORS.muted }}>
                              <Typography variant="caption" className="date-ltr">{formatDate(item.foal_date)}</Typography>
                              {item.owner_name && <Typography variant="caption">{labels.owner}: {item.owner_name}</Typography>}
                              {item.rfid_chip_number && <Typography variant="caption">{labels.rfid}: {item.rfid_chip_number}</Typography>}
                              {item.sire_name && <Typography variant="caption">{labels.sire}: {item.sire_name}</Typography>}
                              {item.dam_name && <Typography variant="caption">{labels.dam}: {item.dam_name}</Typography>}
                            </Stack>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {item.source === 'local' && item.horse_id && (
                              <Button
                                size="small"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => navigate(`/horse-details/${item.horse_id}`)}
                                sx={{ color: COLORS.gold, fontWeight: 900 }}
                              >
                                {labels.openDetails}
                              </Button>
                            )}
                            {item.offspring_url && (
                              <Button
                                size="small"
                                href={item.offspring_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                endIcon={<OpenInNewIcon />}
                                sx={{ color: COLORS.gold, fontWeight: 900 }}
                              >
                                AllBreed
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                  <Divider sx={{ mt: 2, borderColor: COLORS.line }} />
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
}
